<template>
  <div class="case-preview-v2">
    <div v-if="loading" class="d-flex justify-content-center align-items-center vh-100 bg-white">
      <v-loader />
    </div>

    <template v-else-if="caseData">
      <!-- Main Layout Table (for PDF/Print consistency) -->
      <main class="bg-white">
        <div class="container py-32 py-md-64">
          <div class="row justify-content-center">
            <div class="col-lg-10 col-xl-9">



              <!-- 1. ACTIVITY DETAILS / OVERVIEW -->
              <section class="mb-48 pb-32 border-bottom">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-16 mb-16">
                  <div v-if="caseData.modality" class="text-uppercase x-small fw-bold letter-spacing-2 text-muted mb-4">
                    {{ caseData.modality.toUpperCase() }}
                  </div>
                  <div v-if="caseData.travel_type" class="badge bg-black text-white rounded-0 px-16 py-8 text-uppercase x-small fw-bold letter-spacing-1 d-flex align-items-center gap-8">
                    <span :class="getTravelTypeIconClass(caseData.travel_type)" />
                    {{ getTravelTypeLabel(caseData.travel_type) }}
                  </div>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-16 gap-md-24 text-muted small pt-16">
                  <div v-if="caseData.origin?.name || caseData.destination?.name" class="d-flex align-items-center">
                    <span class="mi mi-location me-8 opacity-60 text-black" />
                    <span v-if="caseData.origin?.name" class="fw-semi text-black">{{ caseData.origin.name }}</span>
                    <span v-if="caseData.origin?.name && caseData.destination?.name" class="mi mi-arrow-chevron-forward mx-8 opacity-40 text-xs" />
                    <span v-if="caseData.destination?.name" class="fw-semi text-black">{{ caseData.destination.name }}</span>
                  </div>

                  <div v-if="(caseData.origin?.name || caseData.destination?.name) && (caseData.no_of_days || caseData.caseDays?.length)" class="v-divider"></div>

                  <div v-if="caseData.no_of_days || caseData.caseDays?.length" class="d-flex align-items-center">
                    <span class="mi mi-calandar me-8 opacity-60 text-black" />
                    <span class="fw-semi text-black">{{ caseData.no_of_days || caseData.caseDays?.length }} {{ $t('travelBook.days') || 'Days' }}</span>
                  </div>

                  <div v-if="templateVariableValues.noOfNights > 0" class="v-divider"></div>

                  <div v-if="templateVariableValues.noOfNights > 0" class="d-flex align-items-center">
                    <span class="mi mi-moon me-8 opacity-60 text-black" />
                    <span class="fw-semi text-black">{{ templateVariableValues.noOfNights }} {{ $t('travelBook.nights') || 'Nights' }}</span>
                  </div>
                </div>
              </section>

              <!-- 2. FEATURED IMAGE -->
              <section v-if="caseData.activity?.featuredImage" class="mb-64 mb-md-80 featured-img-v1">
                <img :src="caseData.activity.featuredImage.originalUrl || caseData.activity.featuredImage.original_url || caseData.activity.featuredImage.storageUrl"
                     class="img-fluid w-100 shadow-sm border grayscale-hover"
                     :alt="$t('travelBook.featuredImage')" />
              </section>

              <!-- 3. TOUR RESUME (Table of Contents with Before/After) -->
              <section class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.tourResume') }}</h3>
                <div class="toc-list">
                  <!-- Before Your Trip -->
                  <div
                    v-if="caseData.before_trip_information?.description"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('before')) toggleTocSection('before'); scrollToSection('before')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-calandar text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.beforeTrip') }}</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>

                  <!-- Luggage -->
                  <div
                    v-if="caseData.luggage_information?.description"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('luggage')) toggleTocSection('luggage'); scrollToSection('luggage')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-luggage text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.luggage') }}</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>

                  <!-- Tour Route / Days -->
                  <div
                    v-if="caseData.caseDays?.length"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="scrollToSection('tour-days')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-location text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.tour') }} ({{ caseData.caseDays.length }} {{ $t('travelBook.days') || 'Days' }})</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>

                  <!-- Inclusions & Exclusions -->
                  <div
                    v-if="hasInclusionsInfo"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('inclusions')) toggleTocSection('inclusions'); scrollToSection('inclusions')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-check text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.inclusionsExclusions') }}</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>

                  <!-- After Your Trip -->
                  <div
                    v-if="caseData.after_trip_information?.description"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('after')) toggleTocSection('after'); scrollToSection('after')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-calandar text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.afterTrip') }}</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>

                  <!-- Terms & Conditions -->
                  <div
                    v-if="caseData.terms_conditions_text"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('terms')) toggleTocSection('terms'); scrollToSection('terms')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-document text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.termsAndConditions') || 'Terms & Conditions' }}</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>

                  <!-- Support & Contacts -->
                  <div
                    v-if="caseData.contact_text"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('support')) toggleTocSection('support'); scrollToSection('support')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi mi-profile text-xs opacity-60" />
                      <div class="fw-semi text-black small">{{ $t('travelBook.supportContacts') }}</div>
                    </div>
                    <v-button variant="dark" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-16">
                      {{ $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 4. BEFORE YOUR TRIP (Expandable Accordion) -->
              <section v-if="caseData.before_trip_information?.description" id="section-before" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.beforeTrip') }}</h3>
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('before') }"
                    @click="toggleTocSection('before')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">{{ $t('travelBook.beforeTrip') }}</h3>
                    <div class="mi" :class="isTocSectionOpen('before') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('before')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose text-dark mb-24" v-html="getBeforeTripPreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.before_trip_information.description) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleBeforeTripExpanded"
                    >
                      {{ isBeforeTripExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 4b. LUGGAGE (Expandable Accordion) -->
              <section v-if="caseData.luggage_information?.description" id="section-luggage" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.luggage') }}</h3>
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('luggage') }"
                    @click="toggleTocSection('luggage')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">{{ $t('travelBook.luggage') }}</h3>
                    <div class="mi" :class="isTocSectionOpen('luggage') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('luggage')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose text-dark mb-24" v-html="getLuggagePreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.luggage_information.description) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleLuggageExpanded"
                    >
                      {{ isLuggageExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 5. JOURNEY MAP -->
              <section v-if="itineraryLocations.length" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.map') }}</h3>
                <div class="map-container-v3 border shadow-sm grayscale-hover" style="height: 450px;">
                  <v-map :items="itineraryLocations" :zoom="6" :draw-path="false" />
                </div>
              </section>

              <!-- Activity Days Summary -->
              <section v-if="caseData.caseDays?.length" id="section-tour-days" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.tour') }}</h3>
                <div class="toc-list">
                  <div
                    v-for="(day, index) in caseData.caseDays"
                    :key="day.id"
                    class="toc-item d-flex align-items-center py-16 px-20 border-bottom"
                  >
                    <div class="d-flex align-items-center gap-16 flex-grow-1">
                      <div class="day-label fw-bold x-small text-uppercase letter-spacing-2 text-muted" style="min-width: 60px;">
                        {{ $t('travelBook.day') }} {{ dayNumber(index) }}
                      </div>
                      <span v-if="day.is_stage" :class="getTravelTypeIconClass(caseData.travel_type)" class="text-xs opacity-60" />
                      <div class="day-route fw-semi text-black small">
                        <span v-if="day.origin?.name">{{ day.origin.name }}</span>
                        <span v-if="day.origin?.name && day.destination?.name" class="mi mi-arrow-chevron-forward mx-4 opacity-40 text-xs" />
                        <span v-if="day.destination?.name">{{ day.destination.name }}</span>
                        <span v-if="!day.origin?.name && !day.destination?.name" class="text-muted fst-italic">
                          {{ day.description || $t('travelBook.day') + ' ' + dayNumber(index) }}
                        </span>
                        <span v-if="day.stage_length || day.stageLength" class="ms-8 text-muted fw-normal x-small">
                          ({{ day.stage_length || day.stageLength }} {{ $t('travelBook.km') }})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 7. AFTER YOUR TRIP (Expandable Accordion) -->
              <section v-if="caseData.after_trip_information?.description" id="section-after" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.afterTrip') }}</h3>
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('after') }"
                    @click="toggleTocSection('after')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">{{ $t('travelBook.afterTrip') }}</h3>
                    <div class="mi" :class="isTocSectionOpen('after') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('after')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose text-dark mb-24" v-html="getAfterTripPreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.after_trip_information.description) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleAfterTripExpanded"
                    >
                      {{ isAfterTripExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 9. INCLUSIONS & EXCLUSIONS -->
              <section v-if="hasInclusionsInfo" id="section-inclusions" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.inclusionsExclusions') }}</h3>
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('inclusions') }"
                    @click="toggleTocSection('inclusions')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">{{ $t('travelBook.inclusionsExclusions') }}</h3>
                    <div class="mi" :class="isTocSectionOpen('inclusions') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('inclusions')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="row g-32 g-md-48">
                      <div v-if="caseData.included_trip_information?.description" class="col-lg-6 mb-32 mb-lg-0">
                        <h4 class="fw-bold x-small text-uppercase letter-spacing-1 mb-20">{{ $t('travelBook.whatsIncluded') }}</h4>
                        <div class="prose small text-muted border-start ps-24" v-html="getIncludedTripPreview()"></div>
                      </div>
                      <div v-if="caseData.excluded_trip_information?.description" class="col-lg-6">
                        <h4 class="fw-bold x-small text-uppercase letter-spacing-1 mb-20">{{ $t('travelBook.whatsNotIncluded') }}</h4>
                        <div class="prose small text-muted border-start ps-24" v-html="getExcludedTripPreview()"></div>
                      </div>
                      <div v-if="caseData.optional_trip_information?.description" class="col-12 mt-32 mt-md-48">
                        <h4 class="fw-bold x-small text-uppercase letter-spacing-1 mb-20">{{ $t('travelBook.optionalExtras') }}</h4>
                        <div class="prose small text-muted border-start ps-24" v-html="getOptionalTripPreview()"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 10. TERMS & CONDITIONS -->
              <section v-if="caseData.terms_conditions_text" id="section-terms" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.termsAndConditions') || 'Terms & Conditions' }}</h3>
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('terms') }"
                    @click="toggleTocSection('terms')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">{{ $t('travelBook.termsAndConditions') || 'Terms & Conditions' }}</h3>
                    <div class="mi" :class="isTocSectionOpen('terms') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('terms')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose small text-muted mb-24" v-html="getTermsConditionsPreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.terms_conditions_text) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleTermsExpanded"
                    >
                      {{ isTermsExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 11. SUPPORT & CONTACTS -->
              <section v-if="caseData.contact_text" id="section-support" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.supportContacts') }}</h3>
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('support') }"
                    @click="toggleTocSection('support')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">{{ $t('travelBook.supportContacts') }}</h3>
                    <div class="mi" :class="isTocSectionOpen('support') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('support')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose small text-muted" v-html="getSupportPreview()"></div>
                  </div>
                </div>
              </section>



            </div>
          </div>
        </div>
      </main>

      <!-- Back to Top -->
      <div v-if="showScrollTop" class="back-to-top-v1 hide-print shadow-lg" @click="scrollToTop">
        <span class="mi mi-arrow-chevron-up" />
      </div>
    </template>

    <div v-else class="vh-100 d-flex flex-column align-items-center justify-content-center bg-white text-center p-32">
      <img src="https://site.waw.travel/images/wawlogo.svg" alt="WAW" height="50" class="mb-48 opacity-10" />
      <h2 class="fw-bold text-uppercase letter-spacing-3 small text-muted mb-16">{{ $t('travelBook.itineraryNotFound') }}</h2>
      <p class="text-muted max-w-400 mb-32 small">{{ $t('travelBook.itineraryNotFoundDescription') }}</p>
      <v-button variant="dark" class="rounded-0 px-32 py-12 text-uppercase x-small fw-bold letter-spacing-2" @click="$router.push('/')">
        {{ $t('travelBook.returnHome') }}
      </v-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import api from "@/api";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VMap from "@/components/vendor/VMap.vue";
import { DateTime } from "luxon";
import { findConceptByKey } from "@/helpers/ConceptHelper";
import { replaceTemplateVariables } from "@/composables/useTemplateVariables";

const props = defineProps<{
  template: Record<string, any>;
}>();

const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();
const userStore = useUserStore();

const loading = ref(true);
const caseData = ref<any>(null);
const showScrollTop = ref(false);
const openDays = ref<number[]>([]);
const expandedItems = ref<number[]>([]);
const openTocSections = ref<string[]>(['before', 'luggage', 'after', 'inclusions', 'support', 'terms']);
const openLocationGuides = ref<number[]>([]);
const expandedLocationGuides = ref<number[]>([]);
const isBeforeTripExpanded = ref(true);
const isAfterTripExpanded = ref(true);
const isLuggageExpanded = ref(true);
const isSupportExpanded = ref(true);
const isTermsExpanded = ref(true);
const carouselIndices = ref<Record<number, number>>({});
const locationCarouselIndices = ref<Record<number, number>>({});

const toggleDay = (dayId: number) => {
  const index = openDays.value.indexOf(dayId);
  if (index > -1) openDays.value.splice(index, 1);
  else openDays.value.push(dayId);
};

const isDayOpen = (dayId: number) => openDays.value.includes(dayId);

const toggleExpand = (itemId: number) => {
  const index = expandedItems.value.indexOf(itemId);
  if (index > -1) expandedItems.value.splice(index, 1);
  else expandedItems.value.push(itemId);
};

const isExpanded = (itemId: number) => expandedItems.value.includes(itemId);

const scrollToDay = (dayId: number) => {
  setTimeout(() => {
    const el = document.getElementById(`day-${dayId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
};

const scrollToSection = (sectionId: string) => {
  setTimeout(() => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
};

const truncateDescription = (description: string, limit = 1600) => {
  if (!description) return "";
  if (description.length <= limit) return description;
  const truncated = description.substring(0, limit);
  return truncated + "...";
};

const stripHtml = (html: string | null | undefined) => {
  if (!html) return '';
  return html
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
};

const visibleTextLength = (html: string | null | undefined) => stripHtml(html).length;

const templateVariableValues = computed(() => {
  const days = caseData.value?.no_of_days ?? caseData.value?.caseDays?.length ?? 0;

  // If the backend already computed these values, use them.
  if (caseData.value?.no_of_nights !== undefined) {
    return {
      noOfDays: days,
      noOfNights: Number(caseData.value.no_of_nights) || 0,
      hotelCount: Number(caseData.value.hotel_count) || 0,
      refugeCount: Number(caseData.value.refuge_count) || 0,
      hotelType: 'hotel',
      refugeType: 'refuge',
    };
  }

  let noOfNights = 0;
  let hotelCount = 0;
  let refugeCount = 0;
  for (const day of caseData.value?.caseDays ?? []) {
    for (const item of day.caseDayItems ?? []) {
      let accommodationType = null;

      // Try to determine the supplier type from nested resources first.
      for (const res of item.caseDayItemResources ?? []) {
        const supplierType = res.supplierResource?.supplier?.type ?? res.supplier_info?.type;
        if (supplierType === 'hotel') {
          accommodationType = 'hotel';
        } else if (supplierType === 'refuge') {
          accommodationType = 'refuge';
        }
      }

      // Activity day items may link directly to a supplier without resources.
      if (! accommodationType) {
        const itemData = item.itemData || item.item_data || item;
        const type = itemData?.type || itemData?.supplier?.type || item.type;
        if (type === 'hotel' || type === 'refuge') {
          accommodationType = type;
        }
      }

      if (accommodationType) {
        if (accommodationType === 'hotel') {
          hotelCount++;
        } else if (accommodationType === 'refuge') {
          refugeCount++;
        }
        // Activity day items hold the nights value; case day item resources hold it on the resource.
        noOfNights += item.nights ? Number(item.nights) : 0;
      }
    }
  }
  return {
    noOfDays: days,
    noOfNights,
    hotelCount,
    refugeCount,
    hotelType: 'hotel',
    refugeType: 'refuge',
  };
});

const formatDate = (date: string, format = "dd LLL yyyy") => {
  if (!date) return "";
  return DateTime.fromISO(date).toFormat(format);
};

const dayNumber = (index: number | string) => {
  const n = Number(index) + 1;
  return n < 10 ? `0${n}` : `${n}`;
};

const getTravelTypeLabel = (typeKey: string) => {
  if (!typeKey) return "";
  const concept = findConceptByKey('tour.travel_type', typeKey);
  return (typeof concept === 'object' && concept && 'name' in concept ? concept.name : typeKey.replace('_', ' ').toUpperCase());
};

const getTravelTypeIconClass = (typeKey: string) => {
  const key = (typeKey || "").toLowerCase();
  if (key.includes("bike") || key.includes("cycl")) return "icon icon-bike";
  if (key.includes("walk") || key.includes("hik")) return "icon icon-run";
  return "mi mi-profile";
};

const getLocalizedText = (obj: any, field: string, locale: string): string | null => {
  if (!obj || !Array.isArray(obj.translations)) return null;
  const translation = obj.translations.find((tr: any) => tr.locale === locale && (!tr.field || tr.field === field));
  return translation && field in translation ? translation[field] : null;
};

const normalizeActivityData = (data: any) => {
  if (!data) return null;
  const normalized = { ...data };
  const lang = localStorage.getItem('language') || 'en';

  if (normalized.name) normalized.name = stripHtml(normalized.name);
  if (normalized.tourName || normalized.tour_name) normalized.tour_name = stripHtml(normalized.tourName || normalized.tour_name);
  if (normalized.origin?.name) normalized.origin.name = stripHtml(normalized.origin.name);
  if (normalized.destination?.name) normalized.destination.name = stripHtml(normalized.destination.name);

  // Map activity fields to case fields — SDK returns camelCase keys
  normalized.caseDays = data.activityDays || data.activity_days || [];
  
  if (normalized.caseDays) {
    normalized.caseDays = normalized.caseDays.map((day: any) => {
      const nDay = { ...day };
      nDay.caseDayItems = day.activityDayItems || day.activity_day_items || [];
      nDay.stageLength = day.stageLength || day.stage_length;
      if (nDay.origin?.name) nDay.origin.name = stripHtml(nDay.origin.name);
      if (nDay.destination?.name) nDay.destination.name = stripHtml(nDay.destination.name);
      
      if (nDay.caseDayItems) {
        nDay.caseDayItems = nDay.caseDayItems.map((item: any) => {
          const nItem = { ...item };
          nItem.itemData = item.resource || item.model || item;
          
          if (nItem.itemData) {
             const titleCandidate = getLocalizedText(nItem.itemData, 'title', lang) || nItem.itemData.title || getLocalizedText(nItem.itemData, 'name', lang) || nItem.itemData.name;
             nItem.itemData.title = titleCandidate ? stripHtml(titleCandidate) : '';
             nItem.itemData.name = titleCandidate ? stripHtml(titleCandidate) : '';
             nItem.itemData.description = getLocalizedText(nItem.itemData, 'description', lang) || nItem.itemData.description;
             
             if (nItem.itemData.description) {
               nItem.item_description = nItem.itemData.description;
             }
          }

          nItem.caseDayItemResources = (item.activityDayItemResources || item.activity_day_item_resources || []).map((res: any) => {
            const nRes = { ...res };
            const sRes = res.supplierResource || res.supplier_resource;
            if (sRes) {
              nRes.supplierResource = sRes;
              nRes.resource_name = stripHtml(getLocalizedText(sRes, 'name', lang) || sRes.name);
              const sInfo = sRes.supplier || null;
              if (sInfo) {
                const supplierName = stripHtml(getLocalizedText(sInfo, 'name', lang) || sInfo.name);
                nRes.supplier_info = { ...sInfo, name: supplierName };
              }
              if (sRes.amenities) {
                try {
                  nRes.amenities = typeof sRes.amenities === 'string' ? JSON.parse(sRes.amenities) : sRes.amenities;
                } catch (e) {
                  nRes.amenities = [];
                }
              }
            }
            return nRes;
          });
          
          return nItem;
        });
        
        if (!nDay.stageLength) {
          const stageInfo = nDay.caseDayItems.find((i: any) => i.type === 'stage_information');
          if (stageInfo?.itemData?.stageLength || stageInfo?.itemData?.stage_length) {
            nDay.stageLength = stageInfo.itemData.stageLength || stageInfo.itemData.stage_length;
          }
        }
      }
      return nDay;
    });
  }

  const extractInfoObject = (raw: any, currentLang: string): { description: string; translations?: any[] } | null => {
    if (!raw) return null;
    if (typeof raw === 'string' && raw.trim() !== '') {
      return { description: raw };
    }
    if (typeof raw === 'object') {
      if (Array.isArray(raw.translations) && raw.translations.length > 0) {
        const match = raw.translations.find((t: any) => t.locale === currentLang && t.description && t.description.trim() !== '')
          || raw.translations.find((t: any) => t.locale === 'en' && t.description && t.description.trim() !== '')
          || raw.translations.find((t: any) => t.description && t.description.trim() !== '')
          || raw.translations[0];
        const desc = match?.description || raw.description || '';
        if (desc && desc.trim() !== '') {
          return { description: desc, translations: raw.translations };
        }
      }
      const dictDesc = raw[currentLang]?.description || raw.en?.description || raw.nl?.description || raw.es?.description || (Object.values(raw).find((v: any) => v?.description) as any)?.description;
      if (dictDesc && typeof dictDesc === 'string' && dictDesc.trim() !== '') {
        return { description: dictDesc };
      }
      if (typeof raw.description === 'string' && raw.description.trim() !== '') {
        return { description: raw.description };
      }
    }
    return null;
  };

  normalized.name = data.name || data.tour_name || 'Activity Preview';
  normalized.origin = data.origin || null;
  normalized.destination = data.destination || null;

  normalized.before_trip_information = extractInfoObject(
    data.beforeInformation || data.before_information || data.before_trip || data.beforeTrip || data.before_trip_information || data.beforeTripInformation,
    lang
  );

  normalized.after_trip_information = extractInfoObject(
    data.afterInformation || data.after_information || data.after_trip || data.afterTrip || data.after_trip_information || data.afterTripInformation,
    lang
  );

  normalized.luggage_information = extractInfoObject(
    data.luggageInformation || data.luggage_information || data.luggage_trip || data.luggageTrip || data.luggage_trip_information || data.luggageTripInformation,
    lang
  );

  normalized.included_trip_information = extractInfoObject(
    data.includedInformation || data.included_information || data.included_trip || data.includedTrip || data.included_trip_information || data.includedTripInformation,
    lang
  );

  normalized.excluded_trip_information = extractInfoObject(
    data.excludedInformation || data.excluded_information || data.excluded_trip || data.excludedTrip || data.excluded_trip_information || data.excludedTripInformation,
    lang
  );

  normalized.optional_trip_information = extractInfoObject(
    data.extrasInformation || data.extras_information || data.extras_trip || data.extrasTrip || data.optional_trip_information || data.optionalTripInformation,
    lang
  );

  normalized.contact_text = data.contactText || data.contact_text;
  normalized.terms_conditions_text = data.termsConditionsText || data.terms_conditions_text || data.termsAndConditionsText || data.terms_and_conditions_text;
  normalized.activity = { featuredImage: data.featuredImage || data.featured_image || null };
  normalized.start_date = new Date().toISOString(); 

  normalized.no_of_days = data.noOfDays || data.no_of_days;
  normalized.no_of_nights = data.noOfNights ?? data.no_of_nights;
  normalized.hotel_count = data.hotelCount ?? data.hotel_count;
  normalized.refuge_count = data.refugeCount ?? data.refuge_count;
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + (normalized.no_of_days || normalized.caseDays.length || 0));
  normalized.end_date_formatted = endDate.toISOString();
  normalized.pax_count = 0;
  normalized.modality = data.modality;
  normalized.travel_type = data.travelType || data.travel_type;
  
  normalized.client = {
    name: 'Preview',
    surname: 'Mode',
    email: '',
  };
  normalized.number = 'N/A';

  return normalized;
};

const getItemTypeLabel = (type: string) => {
  const label = t(`travelBook.${type}`);
  return label !== `travelBook.${type}` ? label : type.replace('_', ' ').toUpperCase();
};

const getItemTitle = (item: any) => {
  const data = item.itemData || item.item_data;
  let title = "";
  if (item.type === "stage_information" || item.type === "general_information") {
    title = data?.title || item.type.replace("_", " ").toUpperCase();
  } else if (item.type === "hotel") {
    const resources = item.caseDayItemResources || item.case_day_item_resources || [];
    const firstRes = resources[0];
    const supplierName = firstRes?.supplier_info?.name
      || firstRes?.supplierResource?.supplier?.name
      || data?.supplier?.name
      || null;
    if (supplierName) {
      title = supplierName;
    } else {
      const candidate = data?.title || data?.name || firstRes?.resource_name;
      if (candidate && !/<[^>]+>/.test(candidate) && candidate.length < 80) {
        title = candidate;
      } else {
        title = t('travelBook.hotel');
      }
    }
  } else {
    title = data?.name || item.type.replace("_", " ").toUpperCase();
  }
  return stripHtml(title);
};

const hasInclusionsInfo = computed(() => {
  return !!(
    caseData.value?.included_trip_information?.description ||
    caseData.value?.excluded_trip_information?.description ||
    caseData.value?.optional_trip_information?.description
  );
});

const getDayLocationInfo = (day: any) => {
  const infos: any[] = [];
  if (!day || !day.caseDayItems) return infos;
  day.caseDayItems.forEach((item: any) => {
    if (item.type === 'stage_information' || item.type === 'general_information') {
      const data = item.itemData || item.item_data;
      if (data) {
        const lang = localStorage.getItem('language') || 'en';
        let desc = getLocalizedText(data, 'description', lang) || data.description;
        if (desc) {
          desc = replaceTemplateVariables(desc, templateVariableValues.value);
        }
        infos.push({
          id: item.id,
          type: item.type,
          title: getItemTitle(item),
          locationName: data.location?.name || '',
          description: desc,
          media: data.media || [],
          mapUrl: data.map_url || data.mapUrl,
          siteUrl: data.site_url || data.siteUrl
        });
      }
    }
  });
  return infos;
};

const getGroupedDayItems = (items: any[]) => {
  const grouped: any[] = [];
  if (!items) return grouped;

  const visibleItems = items.filter((item: any) => {
    if (item.is_hidden || item.isHidden) return false;
    if (item.type === 'stage_information' || item.type === 'general_information') return false;
    if (!item.caseDayItemResources || item.caseDayItemResources.length === 0) return false;
    return true;
  });

  visibleItems.forEach((item: any) => {
    const itemType = item.type;
    const resources = item.caseDayItemResources || [];

    if (itemType === 'hotel' || itemType === 'transport' || itemType === 'transfer') {
      const lastGroup = grouped[grouped.length - 1];
      if (lastGroup && lastGroup.type === itemType) {
        lastGroup.items.push(item);
        resources.forEach((res: any) => {
          const resId = res.supplierResource?.id || res.supplier_resource?.id || res.id;
          const existing = lastGroup.groupedResources.find((gr: any) => 
            (gr.supplierResource?.id || gr.supplier_resource?.id || gr.id) === resId
          );
          if (existing) {
            existing.count += 1;
            if (res.nights && res.nights > existing.nights) existing.nights = res.nights;
          } else {
            lastGroup.groupedResources.push({ ...res, count: 1 });
          }
        });
        const note = item.itemData?.notes || item.item_data?.notes;
        if (note && !lastGroup.allNotes.includes(note)) {
          lastGroup.allNotes.push(note);
        }
      } else {
        const initialNotes = [];
        const note = item.itemData?.notes || item.item_data?.notes;
        if (note) initialNotes.push(note);
        
        grouped.push({
          id: `group-${item.id}`,
          type: itemType,
          items: [item],
          groupedResources: resources.map((r: any) => ({ ...r, count: 1 })),
          allNotes: initialNotes
        });
      }
    } else {
      const initialNotes = [];
      const note = item.itemData?.notes || item.item_data?.notes;
      if (note) initialNotes.push(note);

      grouped.push({
        id: `group-${item.id}`,
        type: itemType,
        items: [item],
        groupedResources: resources.map((r: any) => ({ ...r, count: 1 })),
        allNotes: initialNotes
      });
    }
  });

  return grouped;
};

const getItemImages = (item: any) => {
  if (!item) return [];
  const resources = item.caseDayItemResources || item.case_day_item_resources || [];
  for (const res of resources) {
    const supplier = res.supplier_info || res.supplierResource?.supplier;
    if (supplier?.media && supplier.media.length > 0) {
      return supplier.media.map((m: any) => m.original_url || m.storageUrl).filter(Boolean);
    }
  }
  return [];
};

const carouselIndex = (groupId: string | number) => carouselIndices.value[groupId as number] || 0;
const locationCarouselIndex = (locId: number) => locationCarouselIndices.value[locId] || 0;

const nextCarouselImage = (groupId: string | number) => {
  const g = document.querySelector(`[data-group="${groupId}"]`);
  carouselIndices.value[groupId as number] = (carouselIndex(groupId) + 1);
};
const prevCarouselImage = (groupId: string | number) => {
  carouselIndices.value[groupId as number] = Math.max(0, carouselIndex(groupId) - 1);
};

const nextLocationCarouselImage = (locId: number) => {
  locationCarouselIndices.value[locId] = (locationCarouselIndex(locId) + 1);
};
const prevLocationCarouselImage = (locId: number) => {
  locationCarouselIndices.value[locId] = Math.max(0, locationCarouselIndex(locId) - 1);
};

const isTocSectionOpen = (sectionId: string) => openTocSections.value.includes(sectionId);
const toggleTocSection = (sectionId: string) => {
  const index = openTocSections.value.indexOf(sectionId);
  if (index > -1) openTocSections.value.splice(index, 1);
  else openTocSections.value.push(sectionId);
};

const isLocationGuideOpen = (locId: number) => openLocationGuides.value.includes(locId);
const toggleLocationGuide = (locId: number) => {
  const index = openLocationGuides.value.indexOf(locId);
  if (index > -1) openLocationGuides.value.splice(index, 1);
  else openLocationGuides.value.push(locId);
};

const isLocationGuideExpanded = (locId: number) => expandedLocationGuides.value.includes(locId);
const toggleLocationGuideExpand = (locId: number) => {
  const index = expandedLocationGuides.value.indexOf(locId);
  if (index > -1) expandedLocationGuides.value.splice(index, 1);
  else expandedLocationGuides.value.push(locId);
};

const toggleBeforeTripExpanded = () => { isBeforeTripExpanded.value = !isBeforeTripExpanded.value; };
const toggleAfterTripExpanded = () => { isAfterTripExpanded.value = !isAfterTripExpanded.value; };
const toggleLuggageExpanded = () => { isLuggageExpanded.value = !isLuggageExpanded.value; };
const toggleSupportExpanded = () => { isSupportExpanded.value = !isSupportExpanded.value; };
const toggleTermsExpanded = () => { isTermsExpanded.value = !isTermsExpanded.value; };

const getBeforeTripPreview = () => {
  const desc = caseData.value?.before_trip_information?.description;
  if (!desc) return "";
  const replaced = replaceTemplateVariables(desc, templateVariableValues.value);
  return isBeforeTripExpanded.value ? replaced : truncateDescription(replaced);
};

const getAfterTripPreview = () => {
  const desc = caseData.value?.after_trip_information?.description;
  if (!desc) return "";
  const replaced = replaceTemplateVariables(desc, templateVariableValues.value);
  return isAfterTripExpanded.value ? replaced : truncateDescription(replaced);
};

const getLuggagePreview = () => {
  const desc = caseData.value?.luggage_information?.description;
  if (!desc) return "";
  const replaced = replaceTemplateVariables(desc, templateVariableValues.value);
  return isLuggageExpanded.value ? replaced : truncateDescription(replaced);
};

const getSupportPreview = () => {
  const desc = caseData.value?.contact_text;
  if (!desc) return "";
  const replaced = replaceTemplateVariables(desc, templateVariableValues.value);
  return isSupportExpanded.value ? replaced : truncateDescription(replaced);
};

const getTermsConditionsPreview = () => {
  const desc = caseData.value?.terms_conditions_text;
  if (!desc) return "";
  const replaced = replaceTemplateVariables(desc, templateVariableValues.value);
  return isTermsExpanded.value ? replaced : truncateDescription(replaced);
};

const getIncludedTripPreview = () => {
  const desc = caseData.value?.included_trip_information?.description;
  if (!desc) return "";
  return replaceTemplateVariables(desc, templateVariableValues.value);
};

const getExcludedTripPreview = () => {
  const desc = caseData.value?.excluded_trip_information?.description;
  if (!desc) return "";
  return replaceTemplateVariables(desc, templateVariableValues.value);
};

const getOptionalTripPreview = () => {
  const desc = caseData.value?.optional_trip_information?.description;
  if (!desc) return "";
  return replaceTemplateVariables(desc, templateVariableValues.value);
};

const getLocationGuidePreview = (locInfo: any) => {
  if (!locInfo || !locInfo.description) return "";
  return isLocationGuideExpanded(locInfo.id) ? locInfo.description : truncateDescription(locInfo.description);
};

const itineraryLocations = computed(() => {
  const locations = [];
  const origin = caseData.value?.origin;
  const destination = caseData.value?.destination;
  
  if (origin && origin.lat && origin.lng) {
    locations.push({ id: origin.id, lat: origin.lat, lng: origin.lng, name: origin.name, isOrigin: true });
  }
  
  const days = caseData.value?.caseDays || [];
  days.forEach((day: any) => {
    if (day.origin && day.origin.lat && day.origin.lng && !locations.some(l => l.id === day.origin.id)) {
      locations.push({ id: day.origin.id, lat: day.origin.lat, lng: day.origin.lng, name: day.origin.name });
    }
    if (day.destination && day.destination.lat && day.destination.lng && !locations.some(l => l.id === day.destination.id)) {
      locations.push({ id: day.destination.id, lat: day.destination.lat, lng: day.destination.lng, name: day.destination.name });
    }
  });

  if (destination && destination.lat && destination.lng && !locations.some(l => l.id === destination.id)) {
    locations.push({ id: destination.id, lat: destination.lat, lng: destination.lng, name: destination.name, isDestination: true });
  }
  
  return locations;
});

const checkScroll = () => {
  showScrollTop.value = window.scrollY > 400;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const printPage = () => {
  window.print();
};

const contactWhatsApp = () => {
  const phone = '34123456789';
  const text = encodeURIComponent(`Hello WAW Travels! I have a question regarding activity: ${caseData.value?.name}`);
  window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
};

const bookNow = () => {
  alert("Reservation requested for activity.");
};

const loadPreview = async () => {
  const agency = authStore.agency?.slug;
  const activityId = Number(route.params.activitiesId || route.params.id || props.template?.id || userStore.currentTemplate?.id || 0);
  if (!agency || !activityId) {
    if (props.template || userStore.currentTemplate) {
      caseData.value = normalizeActivityData(props.template || userStore.currentTemplate);
    }
    loading.value = false;
    return;
  }
  try {
    loading.value = true;
    const response = await api.activityService.previewActivity(agency, activityId);
    console.log("PREVIEW RESPONSE:", response);
    // Backend now returns the activity directly in response.content.
    const activityData = response?.content ?? response?.data?.data ?? response?.data ?? response;
    
    // Merge with props.template / userStore.currentTemplate as fallback
    const mergedData = {
      ...(props.template || userStore.currentTemplate || {}),
      ...activityData,
    };

    caseData.value = normalizeActivityData(mergedData);
  } catch (e) {
    console.error("Failed to load activity preview:", e);
    if (props.template || userStore.currentTemplate) {
      caseData.value = normalizeActivityData(props.template || userStore.currentTemplate);
    }
  } finally {
    loading.value = false;
  }
};

watch(
  () => [route.params.activitiesId, route.params.id, props.template?.id, userStore.currentTemplate?.id],
  () => {
    loadPreview();
  }
);

onMounted(() => {
  window.addEventListener("scroll", checkScroll);
  loadPreview();
});

onUnmounted(() => {
  window.removeEventListener("scroll", checkScroll);
});
</script>
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

.case-preview-v2 {
  background-color: #fdfdfd;
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
  color: #000000;
  -webkit-font-smoothing: antialiased;
}

/* Utilities */
.fw-extra-bold { font-weight: 800; }
.letter-spacing-1 { letter-spacing: 1px; }
.letter-spacing-2 { letter-spacing: 2px; }
.letter-spacing-3 { letter-spacing: 4px; }
.x-small { font-size: 0.65rem; }
.max-w-400 { max-width: 400px; }
.invert-filter { filter: brightness(0) invert(1); }
.grayscale-hover img {
  filter: grayscale(1);
  transition: filter 0.5s ease;
  &:hover { filter: grayscale(0); }
}
.bg-light-soft { background-color: #fafafa; }
.v-divider { width: 1px; height: 20px; background-color: rgba(0,0,0,0.15); }

/* Header V1 */
.preview-header-v1 {
  background-color: #000;
  border-bottom: 1px solid #333;
}

/* Hero V3 */
.hero-section {
  height: 60vh;
  min-height: 500px;
  background-color: #000;
}
.hero-image-wrapper { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.hero-img { width: 100%; height: 100%; object-fit: cover; filter: brightness(0.7) contrast(1.1); }
.hero-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 100%); }

.hero-title { font-size: clamp(2.5rem, 6vw, 4rem); line-height: 1.1; text-transform: uppercase; }

/* Sections */
.section-title-v1 {
  font-weight: 800;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: #999;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
  margin-bottom: 40px;
}

.section-heading-minimal { border-bottom: none; display: inline-block; }

.overview-content { font-size: 1.25rem; line-height: 1.8; font-weight: 300; }

/* Map */
.map-container-v3 {
  width: 100%;
  background-color: #f5f5f5;
  border: 1px solid #eee;
  :deep(.map) { height: 100%; }
}

/* Timeline Minimal */
.day-number { line-height: 1; min-width: 80px; }
.service-img-wrap { aspect-ratio: 16/10; overflow: hidden; background-color: #f5f5f5; }
.resource-row {
  border-left: 3px solid #000 !important;
  transition: all 0.2s ease;
  &:hover { background-color: #f0f0f0 !important; }
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
}

.carousel-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.carousel-btn {
  background-color: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.2);
    color: #fff;
  }
}

.carousel-counter {
  color: #fff;
  font-size: 0.7rem;
  text-align: center;
  min-width: 45px;
  font-weight: 600;
  letter-spacing: 1px;
}

/* TOC List Styles */
.toc-list {
  border: 1px solid #eee;

  .toc-item {
    transition: all 0.2s ease;
    border-bottom-color: #eee !important;

    &:hover {
      background-color: #f8f8f8;
      padding-left: 24px !important;
    }
  }
}

.stage-length {
  margin-left: auto;
  padding-right: 16px;
}

/* Accordion Styles */
.day-accordion, .accordion-section {
  border: 1px solid #eee;
  border-radius: 0;
}

.accordion-section {
  .accordion-header {
    background-color: #fafafa;
    border-bottom: 1px solid #eee;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
    }

    h3 {
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
  }

  .accordion-body {
    background-color: #fff;
  }
}

.day-accordion-header {
  border-bottom: 1px solid #eee;
  transition: all 0.2s ease;
  &:hover {
    background-color: #f5f5f5;
  }
}

.location-guide-header {
  background-color: #fafafa;
  border-bottom: 1px solid #eee;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
}

/* Day Divider */
.day-divider-v1 {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 40px 0;
  .dot { width: 6px; height: 6px; border-radius: 50%; background-color: #ccc; }
}

/* Location Guide */
.location-info-block {
  border: 1px solid #eee;
  border-left: 4px solid #000 !important;
  margin-top: 48px !important;
}

/* CTA */
.cta-banner-v1 {
  box-shadow: 0 30px 60px rgba(0,0,0,0.1);
  border: 1px solid #eee;
}

/* Back to Top */
.back-to-top-v1 {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 45px;
  height: 45px;
  background-color: #000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1040;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    background-color: #333;
  }
}

/* Print */
@media print {
  .hide-print { display: none !important; }
  .preview-header-v1 { display: none !important; }
  .hero-section { height: 400px !important; background: white !important; }
  .hero-title, .hero-meta, .hero-label { color: black !important; text-shadow: none !important; }
  .hero-overlay { display: none; }
  .day-detail-block { break-inside: avoid; }
  .bg-black { background-color: #000 !important; color: #fff !important; print-color-adjust: exact; }
  .bg-light-soft { background-color: #f8f9fa !important; print-color-adjust: exact; }
  .table-v1 thead { background-color: #000 !important; color: #fff !important; print-color-adjust: exact; }
  .day-divider-v1 { display: none; }
  section { margin-bottom: 40px !important; }
}

/* Responsiveness */
@media (max-width: 991.98px) {
  .hero-section { height: 50vh; min-height: 400px; }
  .hero-title { font-size: 2.5rem; }
  .day-number { font-size: 3rem; min-width: 60px; }
  .section-title-v1 { margin-bottom: 32px; }
}

@media (max-width: 575.98px) {
  .hero-meta { gap: 16px; flex-direction: column; align-items: center; }
  .day-header { flex-direction: column; align-items: flex-start !important; gap: 12px; }
  .day-number { font-size: 2rem; }
  .back-to-top-v1 { bottom: 20px; right: 20px; }
}
</style>
